// NTA Report — pure helpers for the portfolio summary, Top 5, the post-issue
// steps (issued -> accepted -> finalised) and the XLSX sheets. Shared with the
// server route, which validates the same transitions.
import {
  NTA_CSV_HEADERS, NTA_REPORTABLE, NTA_STATUS_LABEL, toNum,
  type NtaLineRow, type NtaResultRow, type NtaResultStatus, type NtaRun,
} from './nta';

// ── Post-issue steps ─────────────────────────────────────────────────────────
// D-2: who accepts the change (retailer or distributor) is unconfirmed; keep wording neutral.

export type NtaAdvanceTo = 'accepted' | 'finalised';

export const NTA_ADVANCE: Record<NtaAdvanceTo, {
  from: NtaResultStatus;
  atColumn: 'accepted_at' | 'finalised_at';
  byColumn: 'accepted_by' | 'finalised_by';
  /** The previous step's date; the new date may not be earlier. */
  prevColumn: 'issued_at' | 'accepted_at';
  action: string;
}> = {
  accepted: { from: 'issued', atColumn: 'accepted_at', byColumn: 'accepted_by', prevColumn: 'issued_at', action: 'Mark accepted' },
  finalised: { from: 'accepted', atColumn: 'finalised_at', byColumn: 'finalised_by', prevColumn: 'accepted_at', action: 'Mark finalised' },
};

/** The step a result can take next, if any. */
export function nextNtaStep(status: NtaResultStatus): NtaAdvanceTo | null {
  if (status === 'issued') return 'accepted';
  if (status === 'accepted') return 'finalised';
  return null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isRealDate = (d: string) => ISO_DATE.test(d) && new Date(`${d}T00:00:00Z`).toISOString().slice(0, 10) === d;

/**
 * Why `to` cannot be recorded on `row` with `date` (YYYY-MM-DD), or null when it can.
 * `today` is the latest allowed date (YYYY-MM-DD).
 */
export function checkNtaAdvance(
  row: { status: string; issued_at?: string | null; accepted_at?: string | null },
  to: string,
  date: string,
  today: string,
): string | null {
  if (to !== 'accepted' && to !== 'finalised') return `Unknown step "${to}"`;
  const step = NTA_ADVANCE[to];
  if (row.status !== step.from) {
    return `Only a result in status "${NTA_STATUS_LABEL[step.from]}" can be marked ${NTA_STATUS_LABEL[to].toLowerCase()}`;
  }
  if (!isRealDate(date)) return 'Date must be a valid YYYY-MM-DD date';
  if (date > today) return 'Date cannot be in the future';
  const prev = row[step.prevColumn]?.slice(0, 10);
  if (prev && date < prev) {
    return `Date cannot be before the ${step.prevColumn === 'issued_at' ? 'issued' : 'accepted'} date (${prev})`;
  }
  return null;
}

/** Today in Australia/Sydney as YYYY-MM-DD — the default and latest allowed step date, on screen and server. */
export const ntaToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(now);

/** The stored timestamp for a picked day: noon UTC, so it reads as the same day in UTC and in Australia. */
export const ntaStampOf = (date: string): string => `${date}T12:00:00.000Z`;

// ── Portfolio filters and summary ────────────────────────────────────────────

const savingOf = (r: Pick<NtaResultRow, 'savings'>) => toNum(r.savings) ?? 0;
const hasSaving = (r: Pick<NtaResultRow, 'savings' | 'status'>) => NTA_REPORTABLE.includes(r.status) && savingOf(r) > 0;
const REACHED_ISSUED: NtaResultStatus[] = ['issued', 'accepted', 'finalised'];
const REACHED_ACCEPTED: NtaResultStatus[] = ['accepted', 'finalised'];

/** List filters. Issued / accepted count every result that has reached that step. */
export const NTA_FILTERS: Array<{ key: string; label: string; match: (r: NtaResultRow) => boolean }> = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'saving', label: 'Saving found', match: hasSaving },
  { key: 'issued', label: 'Issued', match: (r) => REACHED_ISSUED.includes(r.status) },
  { key: 'accepted', label: 'Accepted', match: (r) => REACHED_ACCEPTED.includes(r.status) },
  { key: 'finalised', label: 'Finalised', match: (r) => r.status === 'finalised' },
  { key: 'no_saving', label: 'No saving', match: (r) => r.status === 'no_saving' },
  { key: 'cannot_assess', label: 'Cannot assess', match: (r) => r.status === 'cannot_assess' || r.status === 'failed' },
  { key: 'no_data', label: 'No data', match: (r) => r.status === 'no_data' },
  { key: 'not_viable', label: 'Current tariff not viable', match: (r) => r.baseline_not_viable },
];

const r2 = (n: number) => Math.round(n * 100) / 100;

// No portfolio dollar total: summed unreviewed savings read as a promise (dev ee8d2960).
export type NtaPortfolioSummary = {
  sites: number;
  /** Distinct linked clients with at least one site saving > 0. */
  clientsWithSaving: number;
  sitesWithSaving: number;
  issued: number;
  accepted: number;
  finalised: number;
  cannotAssess: number;
  noData: number;
};

export function ntaPortfolioSummary(rows: NtaResultRow[]): NtaPortfolioSummary {
  const match = (key: string) => rows.filter(NTA_FILTERS.find((f) => f.key === key)!.match);
  const saving = match('saving');
  return {
    sites: rows.length,
    clientsWithSaving: new Set(saving.map((r) => r.site?.client?.id).filter(Boolean)).size,
    sitesWithSaving: saving.length,
    issued: match('issued').length,
    accepted: match('accepted').length,
    finalised: match('finalised').length,
    cannotAssess: match('cannot_assess').length,
    noData: match('no_data').length,
  };
}

export type NtaTopSaving = { id: string; client: string | null; clientId: string | null; nmi: string; saving: number; status: NtaResultStatus };

/** The biggest positive savings, largest first (ties by NMI so the order is stable). */
export function ntaTopSavings(rows: NtaResultRow[], n = 5): NtaTopSaving[] {
  return rows
    .filter(hasSaving)
    .map((r) => ({
      id: r.id, client: r.site?.client?.name ?? null, clientId: r.site?.client?.id ?? null,
      nmi: r.nmi, saving: r2(savingOf(r)), status: r.status,
    }))
    .sort((a, b) => b.saving - a.saving || a.nmi.localeCompare(b.nmi))
    .slice(0, n);
}

// ── XLSX sheets ──────────────────────────────────────────────────────────────

const day = (ts: string | null | undefined) => ts?.slice(0, 10) ?? '';

export function ntaPortfolioXlsxRows(rows: NtaResultRow[]): Record<string, unknown>[] {
  return rows.map((r) => ({
    Client: r.site?.client?.name ?? '',
    NMI: r.nmi,
    'Market Region': r.market_region ?? '',
    'Current Tariff': r.baseline_code ?? '',
    'Best Eligible Tariff': r.best_code ?? '',
    'Current 12m Cost': toNum(r.baseline_total),
    'Best 12m Cost': toNum(r.best_total),
    'Annual Saving': toNum(r.savings),
    Status: NTA_STATUS_LABEL[r.status] ?? r.status,
    Issued: day(r.issued_at),
    Accepted: day(r.accepted_at),
    Finalised: day(r.finalised_at),
    'Current Tariff Not Viable': r.baseline_not_viable ? 'Y' : 'N',
    Reasons: r.reasons.join('; '),
    Error: r.error ?? '',
  }));
}

export function ntaSummaryXlsxRows(s: NtaPortfolioSummary, run: NtaRun | null, filterLabel: string, shown: number): Record<string, unknown>[] {
  return [
    { Metric: 'Run', Value: run ? `${run.label || run.kind} · ${run.run_date}` : '' },
    { Metric: 'Sites assessed', Value: s.sites },
    { Metric: 'Clients with a saving', Value: s.clientsWithSaving },
    { Metric: 'Sites with a saving', Value: s.sitesWithSaving },
    { Metric: 'Issued (incl. accepted and finalised)', Value: s.issued },
    { Metric: 'Accepted (incl. finalised)', Value: s.accepted },
    { Metric: 'Finalised', Value: s.finalised },
    { Metric: 'Cannot assess', Value: s.cannotAssess },
    { Metric: 'No data', Value: s.noData },
    { Metric: 'Results sheet filter', Value: `${filterLabel} (${shown} rows)` },
  ];
}

export function ntaTopXlsxRows(top: NtaTopSaving[]): Record<string, unknown>[] {
  return top.map((t, i) => ({
    Rank: i + 1, Client: t.client ?? '', NMI: t.nmi, 'Annual Saving': t.saving, Status: NTA_STATUS_LABEL[t.status] ?? t.status,
  }));
}

/** One NMI's lines with the 26 Energetica headers, as the CSV. */
export function ntaLinesXlsxRows(lines: NtaLineRow[]): Record<string, unknown>[] {
  return lines.map((l) => {
    const values = [
      l.country, l.market, l.market_region, l.service_point, l.scenario, l.eligible ? 'Y' : 'N', l.ineligible_reason,
      l.period_from, l.period_to, l.product_class, l.product_type, l.product, l.component, l.component_type,
      l.component_class, toNum(l.quantity), l.quantity_unit, l.rate_unit, toNum(l.rate), l.adjustments, l.adjusted_rate_unit,
      toNum(l.adjusted_rate), toNum(l.amount), l.source_from, l.source_to, l.error,
    ];
    return Object.fromEntries(NTA_CSV_HEADERS.map((h, i) => [h, values[i] ?? '']));
  });
}
